import { useCallback } from 'react';
import { useTranslation } from '@shared/core';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import type { GetRegistryDataQuery } from '../../../apps/registry/guest/GuestRegistry.graphql.generated';
import { EventPageType, EventPageVisibility, PageKind } from '@graphql/generated-base';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';
import { useEventInfo } from '@shared/utils/eventInfo';

export const useAppPage = () => {
  const { tq } = useTranslation('eventSiteApps');
  const disableAddAppPage = useProFeature(EventFeature.disableAddAppPage);
  const { eventInfo } = useEventInfo();
  const hideDownloadAppPage = eventInfo?.settings.hideDownloadAppPage ?? false;

  // Stable identity matters well beyond this file: GuestSite.controller feeds addAppPage into a
  // useMemo whose result becomes the guest site's `event` object. A fresh function each render
  // invalidated that memo and three more downstream, so `event` was a new object on every render
  // and every consumer keyed on its identity re-did its work.
  const addAppPage = useCallback(
    (eventHandle: string | undefined, event: GuestSiteEvent | GetRegistryDataQuery['eventByName'] | undefined | null) => {
      const newPages = event?.pages ? [...event.pages] : [];

      if (!disableAddAppPage && !hideDownloadAppPage) {
        newPages.push({
          id: 'app',
          disabled: false,
          mobilePhoto: null,
          pageSlug: 'app',
          pageTitle: tq`Get the app`,
          photoV2: null,
          subTitle: tq`Download Joy and join our app using our event handle ${eventHandle}. View and share photos and stay up-to-date with details on the go.`,
          type: EventPageType.app,
          pageKind: PageKind.systemLegacy,
          isPageContainer: false,
          visibility: EventPageVisibility.public,
          private: false,
        });
      }

      return newPages;
    },
    [disableAddAppPage, hideDownloadAppPage, tq],
  );

  return addAppPage;
};
